// Keeps the phone app shell pinned to the screen.
//
// Below 820px the shell is a 100dvh flex column and <main> is the only
// scroller (kit.css), so the window itself should never move. iOS still
// scrolls the window in one case: when the keyboard opens it shifts the page
// to keep the focused field visible, and when the keyboard closes it often
// leaves that shift in place. The whole app then sits too high: the blue
// header is off the top and a blank band shows under the bottom tab bar
// (Paul Merrill's iPhone 16 Pro Max screenshot, Jill's iPhone 15 Pro,
// 2026-09-06; the earlier rounds of this fight are in kit.css). Pilots were
// dragging the tab bar back down by hand.
//
// Rule: on a narrow screen, whenever the window has scrolled and no text
// field is focused, put it back to 0,0. While a field IS focused the shift is
// what keeps the field above the keyboard, so leave it alone until blur.
// Desktop (the window is the real scroller) is never touched.
//
// Hold: never move the window while a finger is on the screen. A signature
// stroke reads the pad's position on every point; a window that jumps under
// the finger mid-stroke and snaps back the same frame is one candidate for
// the runaway signatures of 2026-09-18 (John Herring, Capt Joel). The reset
// waits for the finger to lift, then runs as before. A finger held down for
// a long time (or a lost pointerup) does not hold the window forever.

const NARROW = "(max-width: 819.98px)";
const HOLD_MAX_MS = 15000;
let fingersDown = 0;
let fingerSince = 0;

function isTyping() {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

function fingerOnScreen() {
  return fingersDown > 0 && Date.now() - fingerSince < HOLD_MAX_MS;
}

function reset() {
  if (!window.matchMedia(NARROW).matches) return;
  if (isTyping()) return;
  if (fingerOnScreen()) return;
  if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
}

export function installKeepShellPinned() {
  if (typeof window === "undefined") return () => {};
  let timer = null;
  // Keyboard dismissal animates; check once it has settled, then once more
  // in case the first check landed mid-animation.
  const later = () => {
    clearTimeout(timer);
    timer = setTimeout(() => { reset(); timer = setTimeout(reset, 350); }, 120);
  };
  const fingerDown = () => { fingersDown += 1; if (fingersDown === 1) fingerSince = Date.now(); };
  const fingerUp = () => {
    fingersDown = Math.max(0, fingersDown - 1);
    if (fingersDown === 0) later(); // the held reset runs now
  };
  const fingersGone = () => { fingersDown = 0; };
  window.addEventListener("scroll", reset, { passive: true });
  document.addEventListener("focusout", later, true);
  window.visualViewport?.addEventListener("resize", later);
  window.addEventListener("orientationchange", later);
  document.addEventListener("pointerdown", fingerDown, { capture: true, passive: true });
  document.addEventListener("pointerup", fingerUp, { capture: true, passive: true });
  document.addEventListener("pointercancel", fingerUp, { capture: true, passive: true });
  window.addEventListener("blur", fingersGone);
  return () => {
    clearTimeout(timer);
    window.removeEventListener("scroll", reset);
    document.removeEventListener("focusout", later, true);
    window.visualViewport?.removeEventListener("resize", later);
    window.removeEventListener("orientationchange", later);
    document.removeEventListener("pointerdown", fingerDown, { capture: true });
    document.removeEventListener("pointerup", fingerUp, { capture: true });
    document.removeEventListener("pointercancel", fingerUp, { capture: true });
    window.removeEventListener("blur", fingersGone);
  };
}
